import { defineStore } from 'pinia';
import { ref } from 'vue';
import {
  buildDiagnosticReport, currentReplayId, ErrorKind, ErrorTier, formatDiagnosticReport,
  isReportingAvailable, normalizeError, readTrail, redactQuery, reportToSentry, shouldPresent,
  submitFeedbackToSentry, summarizeForUser, translateErrorKey, withReportGuard,
  type DiagnosticContext, type DiagnosticReport, type NormalizeContext, type NormalizedError
} from '@/errors';
import router from '@/router';
// The module, not the `@/services` barrel: that barrel reaches services which reach stores, and this
// store is imported from `main.ts` before the app is installed.
import SupportService from '@/services/supportService';
import { prepareErrorToast } from '@/models/toast';
import { SENTRY_ORG_URL, SUPPORT_EMAIL } from '@/utils/constants';
import { useAppStore } from './app';
import { useStudyStore } from './study';
import { useUserStore } from './user';

/**
 * Routes an error to the right presentation tier, and owns the report-to-support flow.
 *
 * This is the *only* module allowed to know about toasts, translated copy and Pinia — `src/errors/*`
 * stays framework-free so it can run before the app is installed. See `src/errors/index.ts`.
 *
 * Note: this store deliberately does **not** use `LoadingStateModel` for `isSubmitting`.
 * `loadingState.ts` imports this store, so depending on it here would be a genuine cycle rather than
 * the harmless live-binding kind.
 */

/** Mail clients truncate long URLs, so the body is a summary and the clipboard holds the full report. */
const MAILTO_BODY_LIMIT = 1800;

/** Long enough to read "your report was sent" before the app restarts under it. */
const RELOAD_AFTER_SEND_MS = 2000;

/**
 * `sent` means support has the report. `emailed` means the user's mail client has it and the send is
 * now theirs to finish. `failed` means neither — the dialog stays open with its buttons, because the
 * mail-client fallback cannot be opened for them: `window.open` needs the click's transient user
 * activation, and the API round-trip has already spent it (see `emailSupport`).
 */
export type SubmitState = 'idle' | 'sending' | 'sent' | 'emailed' | 'failed';

export interface ReportTarget {
  error: NormalizedError;
  report: DiagnosticReport;
}

export interface HandleOptions extends NormalizeContext {
  /** Called by the fatal screen's "Try again". Defaults to a reload. */
  retry?: () => void | Promise<void>;
}

export const useErrorStore = defineStore('error', () => {
  const fatal = ref<NormalizedError | undefined>();
  const reportTarget = ref<ReportTarget | undefined>();
  const submitState = ref<SubmitState>('idle');
  /** Set between a sent report and the reload it triggers, so the dialog can say so. */
  const reloadPending = ref(false);

  let fatalRetry: (() => void | Promise<void>) | undefined;

  /**
   * Recent errors by reference, so the "Report this" action on a toast can find its error again — a
   * toast carries only the reference string, not the object. Capped: this is a lookup, not a log.
   */
  const recent = new Map<string, NormalizedError>();
  const RECENT_LIMIT = 10;

  const remember = (normalized: NormalizedError) => {
    recent.set(normalized.reference, normalized);
    if (recent.size > RECENT_LIMIT) {
      recent.delete(recent.keys().next().value as string);
    }
  };

  /**
   * One string out of a route value.
   *
   * vue-router types both params and query as `string | string[] | null`: a repeatable segment or a
   * duplicated param (`?caseId=a&caseId=b`) yields an array, and a valueless param yields `null`. The
   * previous `as Record<string, string>` cast asserted that away, so an array could reach a Sentry tag
   * — where a non-string value is dropped or throws in formatting — and an empty string could stand in
   * for a real identifier.
   */
  const firstRouteValue = (value: unknown): string | undefined => {
    const single = Array.isArray(value) ? value[0] : value;
    return typeof single === 'string' && single !== '' ? single : undefined;
  };

  /** The same flattening across a whole params object, so the report's own field is honestly typed. */
  const flattenRouteValues = (source: Record<string, unknown>): Record<string, string> => {
    const flat: Record<string, string> = {};
    Object.entries(source).forEach(([name, value]) => {
      const single = firstRouteValue(value);
      if (single != null) {
        flat[name] = single;
      }
    });
    return flat;
  };

  /**
   * Gather everything a support engineer needs to reproduce the issue.
   *
   * Each read is wrapped separately: this runs *during* a failure, so a half-initialized store must
   * cost one field rather than the whole report. Context-gathering is the likeliest place for a crash
   * inside the crash handler now that the payload is this detailed.
   */
  const buildContext = (occurrences: number): DiagnosticContext => {
    const context: DiagnosticContext = {
      release: __APP_RELEASE__,
      commit: __APP_COMMIT__,
      environment: import.meta.env.VITE_APP_ENVIRONMENT || 'staging',
      occurrences,
      trail: readTrail(),
      replayId: currentReplayId()
    };

    try {
      const route = router.currentRoute.value;
      const params = route.params;
      const query = route.query;

      context.route = {
        name: typeof route.name === 'string' ? route.name : undefined,
        // Indexed rather than `.at(-1)`: the build targets esnext with no polyfills, and on an engine
        // without `Array.prototype.at` (iOS below 15.4) the throw is swallowed by the catch below —
        // silently costing the whole route block and all four identifiers, on exactly the old browsers
        // whose failures most need reporting.
        pathTemplate: route.matched[route.matched.length - 1]?.path,
        path: route.path,
        params: flattenRouteValues(params),
        // Allowlisted: the report travels to Sentry and to support email, and a route query can hold
        // a free-text filter or a link token. `redactQuery` keeps every value of a repeated param;
        // the single-valued identifier fields below take the first.
        query: redactQuery(query)
      };

      // The reproduction ids come from the route rather than the entry store: the route already
      // carries all of them (`ENTRY_DIALOG_QUERY` plus the route params), and reading them here keeps
      // this store off the entry store's import graph.
      context.studyIdentifier = firstRouteValue(params.studyIdentifier) ??
        firstRouteValue(params.studyId) ?? firstRouteValue(query.studyId);
      context.caseIdentifier = firstRouteValue(params.identifier) ??
        firstRouteValue(params.caseId) ?? firstRouteValue(query.caseId);
      context.entryIdentifier = firstRouteValue(params.entryIdentifier) ?? firstRouteValue(query.entryId);
      context.formIdentifier = firstRouteValue(params.formIdentifier) ?? firstRouteValue(query.formId);
    } catch {
      // No router context — leave the route fields unset.
    }

    try {
      const study = useStudyStore().selectedStudy;
      context.studyB64Identifier = study?.b64Identifier;
      context.studyLongitudinal = study?.longitudinal;
      // Values, not just keys: cfrPart11 and longitudinal change code paths.
      context.studyFlags = study?.flags as Record<string, unknown> | undefined;
      context.studyIdentifier = context.studyIdentifier ?? study?.identifier;
    } catch {
      // Study not selected yet.
    }

    try {
      // The UserModel, never authStore.userData — that object holds the HMAC signing material.
      const user = useUserStore().user;
      context.userIdentifier = user?.identifier;
      context.userEmail = user?.email;
      context.organizationIdentifier = user?.organizationIdentifier;
    } catch {
      // Not signed in yet.
    }

    return context;
  };

  const showToast = (normalized: NormalizedError, escalated: boolean): boolean => {
    const summary = summarizeForUser(normalized);
    // The escalated copy promises that reporting will help, which is only honest when there is a
    // report button to press. An expected failure repeated five times — a save that keeps failing
    // validation, a permission that keeps being refused — keeps its own message instead: that one
    // carries the server's own explanation, which is what the user can actually act on. Escalation
    // still shows, through the toast staying until dismissed.
    const promiseAReport = escalated && normalized.isReportable;
    try {
      return useAppStore().addToast(prepareErrorToast({
        title: summary.title,
        message: promiseAReport ? translateErrorKey('error_repeated_message') : summary.message,
        reference: normalized.reference,
        canReport: normalized.isReportable,
        // A reportable failure stays until dismissed; expected copy can time out, unless it keeps
        // happening — then it is worth noticing.
        sticky: normalized.isReportable || escalated
      }));
    } catch {
      // The store itself was unavailable. Fall through to the fatal screen so the user sees
      // *something* rather than nothing.
      return false;
    }
  };

  /** The single entry point. Normalizes, reports once, then presents at the tier the taxonomy chose. */
  const handle = (error: unknown, options: HandleOptions = {}): NormalizedError => {
    const normalized = normalizeError(error, options);

    withReportGuard(() => {
      const decision = shouldPresent(normalized);
      const context = buildContext(decision.occurrences);
      const report = buildDiagnosticReport(normalized, context);

      normalized.sentryEventId = reportToSentry(normalized, report);
      remember(normalized);

      // A lapsed session is already being handled by the interceptor's redirect.
      if (normalized.kind === ErrorKind.AUTH_EXPIRED || normalized.tier === ErrorTier.SILENT) {
        return;
      }
      if (normalized.tier === ErrorTier.INLINE) {
        // The caller renders <ErrorState> from the loader it owns.
        return;
      }
      if (normalized.tier === ErrorTier.FATAL) {
        if (!fatal.value) {
          // First fatal wins — a cascade must not keep re-rendering the screen.
          fatal.value = normalized;
          fatalRetry = options.retry;
        }
        return;
      }
      // Nothing to toast onto: the error screen has replaced the view, so a stack of toasts over it
      // is noise the user cannot act on.
      if (fatal.value) {
        return;
      }
      if (decision.present && !showToast(normalized, decision.escalated) && !fatal.value) {
        fatal.value = normalized;
        fatalRetry = options.retry;
      }
    });

    return normalized;
  };

  /**
   * Start the app over.
   *
   * A whole-document reload rather than a router push: the point is to discard whatever state the
   * failure left behind, and a route change keeps all of it. Exposed as an action rather than being
   * called from the component so the reporting screens have one seam to reload through, and so a
   * test can watch it.
   */
  const reloadApp = () => window.location.reload();

  /**
   * Whether the screen the user is looking at is beyond saving — and so whether a sent report should
   * end in a reload rather than in a button offering one.
   *
   * Two shapes qualify. `fatal` is the crash screen, which has replaced the router outlet. A `RENDER`
   * error is the page-level `ErrorBoundary`'s fallback: the INLINE tier, so `fatal` is unset, but the
   * app's only boundary wraps the router outlet in `App.vue`, so a structural render failure has
   * already taken the whole view with it. That is the screen whose sole action is "Report a problem".
   *
   * Everything else reached this dialog from a toast on a page that still works — a save that failed,
   * a permission refused. A coordinator with a half-filled CRF open would lose it to a reload the
   * failure itself did not cost them, so those get the button and no timer.
   */
  const isViewUnusable = (error: NormalizedError): boolean =>
    !!fatal.value || error.kind === ErrorKind.RENDER;

  /** Accepts the error itself, or the reference a toast carries. Falls back to the fatal error. */
  const openReport = (error?: NormalizedError | string) => {
    const target = typeof error === 'string' ? recent.get(error) : error ?? fatal.value;
    if (!target) {
      return;
    }
    submitState.value = 'idle';
    reloadPending.value = false;
    reportTarget.value = {
      error: target,
      report: buildDiagnosticReport(target, buildContext(1))
    };
  };

  const closeReport = () => {
    reportTarget.value = undefined;
    submitState.value = 'idle';
  };

  const copyDiagnostics = async (note: string = ''): Promise<boolean> => {
    if (!reportTarget.value) {
      return false;
    }
    try {
      await navigator.clipboard.writeText(formatDiagnosticReport(reportTarget.value.report, note));
      return true;
    } catch {
      return false;
    }
  };

  /**
   * Hand the report to the user's mail client.
   *
   * Always available, not just a fallback. The body carries the reference, the Sentry event id and a
   * search link; the clipboard carries the full payload, because a long `mailto:` gets truncated.
   *
   * Synchronous on purpose. `window.open` needs the click's transient user activation, and Safari drops
   * that at the end of the task — awaiting the clipboard write first was enough to get the mail client
   * treated as an unsolicited popup and blocked. This is the last channel a user has for reporting, so
   * it cannot be the one that depends on a convenience completing first.
   */
  const emailSupport = (note: string = ''): void => {
    if (!reportTarget.value) {
      return;
    }
    const { report } = reportTarget.value;
    // Fired, not awaited: the body below already carries the report, so a failed or slow copy costs a
    // convenience rather than the whole email.
    void copyDiagnostics(note);

    const searchLink = `${SENTRY_ORG_URL}/issues/?query=${encodeURIComponent(`error.reference:${report.reference}`)}`;
    const body = [
      note ? `What I was doing:\n${note}\n` : '',
      `Reference: ${report.reference}`,
      report.sentryEventId ? `Sentry event: ${report.sentryEventId}` : '',
      `Sentry search: ${searchLink}`,
      '',
      'Full technical details are below, and a complete copy may also be on my clipboard.',
      '',
      formatDiagnosticReport(report).slice(0, MAILTO_BODY_LIMIT)
    ].filter(Boolean).join('\n');

    const subject = `Studypages Data error ${report.reference}`;
    window.open(`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
    submitState.value = 'emailed';
  };

  /**
   * Send the report without the user having to compose anything.
   *
   * Two channels, and they are not alternatives:
   *
   *  - Sentry feedback, when there is a DSN. This is what associates the note with the error event
   *    and its session replay, so it goes first and synchronously.
   *  - `POST support/error-report`, which is what actually emails support. It holds the recipient
   *    list in its own env, so who gets a crash report is not a frontend deploy.
   *
   * Success is the email going out, because that is the channel support watches; a DSN-less build
   * (local dev, CI) therefore still reports properly rather than falling through to the mail client.
   * A failure lands on `failed` rather than opening the mail client, because after this `await` the
   * click's user activation is gone and Safari would block the popup — the dialog keeps its
   * "Email support instead" button, and that click brings a fresh activation.
   */
  const submitReport = async (note: string = ''): Promise<void> => {
    // `sending` is the guard, not just a spinner flag: past this point the report goes out on two
    // channels, and a second pass would file a second Sentry feedback and send support a duplicate
    // email. The dialog's Send button disables itself on the same state, so this is the store
    // holding the invariant rather than borrowing it from one button's `:loading` binding.
    if (!reportTarget.value || submitState.value === 'sending') {
      return;
    }
    const { error, report } = reportTarget.value;

    submitState.value = 'sending';

    if (isReportingAvailable()) {
      submitFeedbackToSentry({
        report,
        note,
        email: report.user.email,
        formatted: formatDiagnosticReport(report)
      });
    }

    // The note travels as its own field, not folded into the text: the server renders it above the
    // diagnostics, where a reader sees it before the stack rather than after it.
    const emailed = await SupportService.sendErrorReport({
      reference: report.reference,
      report: formatDiagnosticReport(report),
      note: note || undefined,
      kind: report.kind,
      sentryEventId: report.sentryEventId,
      replayId: report.replayId,
      environment: report.environment,
      release: report.release
    });

    submitState.value = emailed ? 'sent' : 'failed';

    // A report sent from a screen that cannot be recovered ends in a reload: restarting the app is
    // the only thing left to do, and asking for one more button press to get it is ceremony. See
    // `isViewUnusable` for which screens those are, and why the others keep their page.
    if (emailed && isViewUnusable(error)) {
      reloadPending.value = true;
      window.setTimeout(reloadApp, RELOAD_AFTER_SEND_MS);
    }
  };

  const clearFatal = () => {
    fatal.value = undefined;
    fatalRetry = undefined;
  };

  const retryFatal = async (): Promise<void> => {
    const retry = fatalRetry;
    clearFatal();
    if (!retry) {
      window.location.reload();
      return;
    }
    await retry();
  };

  return {
    fatal,
    reportTarget,
    submitState,
    reloadPending,
    handle,
    reloadApp,
    openReport,
    closeReport,
    submitReport,
    emailSupport,
    copyDiagnostics,
    clearFatal,
    retryFatal
  };
});
